import React from 'react'

import { useNavigate } from 'react-router-dom'
import { saveAs } from 'file-saver';

import ENGTimesheetDaily from './ENGTimesheetDaily.jsx'
import ENGTimesheetJob from './ENGTimesheetJob.jsx'
import { Workbook } from 'exceljs';

import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AllInboxIcon from '@mui/icons-material/AllInbox';
import ENGTimesheetsSubmissions from './ENGTimesheetsSubmissions.jsx';

export default function ENGTimesheetsFull({ division = "", viewer }) {

  const [timesheets, setTimesheets] = React.useState([]);
  const [showSearchBar, setShowSearchBar] = React.useState(false);

  const [filteredList, setFilteredList] = React.useState([]);
  const [selectedDateRange, setSelectedDateRange] = React.useState("");
  const [selectedEmployee, setSelectedEmployee] = React.useState('');
  const [selectedJobNumber, setSelectedJobNumber] = React.useState('');

  const [masterData, setMasterData] = React.useState({});

  // --- Cleaning up the master data from the backend --- 

  // Formatting the jobNumbers correctly

  function formatJobNumber(jobNumber) {
    // Case 3: If jobNumber does not contain '23', prepend '23-' and pad with zeros

    jobNumber = jobNumber.trim();

    if (jobNumber === "N/A") {
      return '23-001'
    }

    if (!jobNumber.includes('23')) {
      let paddedNumber = jobNumber.padStart(4 - jobNumber.length, '0');
      return '23-' + paddedNumber;
    }

    if (!jobNumber.startsWith("23")) {
      return "23-" + jobNumber
    }

    // Case 2: If jobNumber starts with '23' and contains another '23', add a dash between
    if (jobNumber.startsWith('23') && jobNumber.lastIndexOf('23') !== 0) {
      return jobNumber.slice(0, 2) + '-' + jobNumber.slice(2);
    }

    // Case 1: If jobNumber starts with '23' but does not have a dash, add a dash after '23'
    if (jobNumber.startsWith('23') && !jobNumber.includes('-')) {
      return jobNumber.slice(0, 2) + '-' + jobNumber.slice(2);
    }

    // Default case: return original jobNumber
    return jobNumber;
  }

  React.useEffect(() => {
    let newMasterData = {};
    timesheets.forEach(entry => {
      const [day, month, year] = entry.Date.split("/").map(Number);

      const date = new Date(year, month - 1, day);

      const startOfWeek = getMonday(date);
      const endOfWeek = getSunday(date);

      const key = `${startOfWeek.getDate().toString().padStart(2, '0')}/${(startOfWeek.getMonth() + 1).toString().padStart(2, '0')}/${startOfWeek.getFullYear()} - ${endOfWeek.getDate().toString().padStart(2, '0')}/${(endOfWeek.getMonth() + 1).toString().padStart(2, '0')}/${endOfWeek.getFullYear()}`;

      if (!newMasterData[key]) {
        newMasterData[key] = [];
      }

      const jobDetails = JSON.parse(entry.JobDetails);

      // Map each job's details from an object to an Object with descriptive keys
      const jobDetailsEntries = jobDetails.map((job, index) => {
        const [jobNumber, jobDescription, startTime, endTime, lunch, totalHours] = job.data;

        let correctedJobNumber = jobNumber;
        if (job.id !== "Total") {
          correctedJobNumber = formatJobNumber(jobNumber);
        }

        return {
          id: index, // Add id to each jobDetail
          jobNumber: correctedJobNumber,
          jobDescription,
          startTime,
          endTime,
          lunch,
          totalHours,
        };
      });

      newMasterData[key].push({
        ...entry,
        jobDetails: jobDetailsEntries,
      });
    });
    setMasterData(newMasterData);
  }, [timesheets]);

  // --- Data Formatting --- 
  function getMonday(d) {
    d = new Date(d);
    var day = d.getDay(),
      diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  function getSunday(d) {
    d = new Date(d);
    d.setDate(d.getDate() + (7 - d.getDay() % 7) % 7);
    return d;
  }

  // --- Function Handlers --- 
  const handleSearch = () => {
    setShowSearchBar(!showSearchBar);
  };

  const handleRefresh = () => {
    getTimesheets();

    setSelectedEmployee("")
    setSelectedDateRange("")
    setSelectedJobNumber("")
  }

  // Function for sorting the date ranges

  const doFilter = React.useCallback(() => {
    let filteredMasterData = Object.entries(masterData).reduce((acc, [weekRange, entries]) => {
      // Apply the selectedDateRange filter based on weekRange, not individual entry dates
      if (selectedDateRange && weekRange !== selectedDateRange) {
        return acc;
      }

      let filteredData = entries.reduce((filteredEntries, entry) => {
        // Apply the selectedEmployee filter
        if (selectedEmployee && entry.FullName.toLowerCase() !== selectedEmployee.toLowerCase()) {
          return filteredEntries;
        }

        // Filter jobDetails based on selectedJobNumber, if any
        const filteredJobDetails = selectedJobNumber ? entry.jobDetails.filter(detail => detail.jobNumber === selectedJobNumber) : entry.jobDetails;

        // If there's at least one job detail, include the entry in the results
        if (filteredJobDetails.length > 0) {
          filteredEntries.push({ ...entry, jobDetails: filteredJobDetails });
        }

        return filteredEntries;
      }, []);

      // Include the week range and its data only if there's at least one match
      if (filteredData.length > 0) {
        acc[weekRange] = filteredData;
      }

      return acc;
    }, {});

    // Filter set based on date first:
    const sortedKeys = Object.keys(filteredMasterData).sort((a, b) => {
      const startDateA = new Date(a.split(' - ')[0].split('/').reverse().join('-'));
      const startDateB = new Date(b.split(' - ')[0].split('/').reverse().join('-'));

      return startDateB - startDateA;
    });

    const sortedData = {};
    for (let key of sortedKeys) {
      sortedData[key] = filteredMasterData[key];
    }

    setFilteredList(sortedData);
  }, [selectedEmployee, selectedJobNumber, selectedDateRange, masterData]);

  React.useEffect(() => {
    doFilter()
  }, [doFilter])
  //

  const getTimesheets = async () => {
    // https://ic360portalbackend.azurewebsites.net
    await fetch("https://ic360portalbackend.azurewebsites.net/api/ENG/Timesheets/allTimesheets")
      .then(response => response.json())
      .then(data => {
        setTimesheets(data)
      })

  }

  React.useEffect(() => {
    getTimesheets()
  }, [])

  // --- Cleaning up data for filters ---

  const dateRanges = [...new Set(Object.keys(masterData))];

  const jobNumbers = [...new Set(
    Object.values(masterData)
      .flat()
      .flatMap(data => data.jobDetails.map(jobDetail => jobDetail.jobNumber))
  )].filter(num => num !== 'N/A').sort((a, b) => a - b);

  const employeeNames = [...new Set(Object.values(masterData).flat().map(data => data.FullName))];

  const navigate = useNavigate()

  // --- Exporting Data to EXCEL ---
  function sanitizeString(str) {
    return str.trim().replace(/\s+/g, ' ');
  }

  const filterExportViewer = (data, timesheetWeek) => {
    if (viewer === 'accounts') {
      handleExportAccounts(data, timesheetWeek)
    }

    else {
      handleExportENG(data, timesheetWeek)
    }
  }

  const handleExportAccounts = async (data, timesheetWeek) => {

    // Split on " - " to get start and end dates
    const [startDate, endDate] = timesheetWeek.ts.split(' - ');

    // Extract day and month from each date
    const [startDay, startMonth] = startDate.split('/').slice(0, 2);
    const [endDay, endMonth] = endDate.split('/').slice(0, 2);

    // Construct and return the new string
    const fileName = `Eng Timesheet ${startDay}-${startMonth} to ${endDay}-${endMonth}`;

    // DATA TRANSFORMATION HERE
    const functionData = [...data]; // shallow copy

    const sortedData = functionData.sort((a, b) => {
      if (a.FullName < b.FullName) return -1;
      if (a.FullName > b.FullName) return 1;
      return 0;
    });

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Exported Data');

    // Headers
    const headers = ["FullName", "Job Description", "Job Number"];
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    days.forEach(day => {
      headers.push(day, `${day} Start`, `${day} Finish`, `${day} Lunch`, `${day} TotalHours`, `${day} Ord`, `${day} x1.5`, `${day} x2`);
    });

    headers.push("TOTAL HOURS");
    headers.push("Total Ord")
    headers.push("Total x1.5")
    headers.push("Total x2")
    headers.push("Rate")
    headers.push("Total Comp")

    worksheet.columns = headers.map(header => ({ header, key: header, width: 15 }));

    // Centering columns
    worksheet.getColumn('Job Number').alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getColumn('TOTAL HOURS').alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getColumn('Total Ord').alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getColumn("Total x1.5").alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getColumn("Total x2").alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getColumn("Rate").alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getColumn("Total Comp").alignment = { vertical: 'middle', horizontal: 'center' };

    // Bold the headers
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
    });

    // Function to get day of week from date
    const getDayOfWeek = (dateStr) => {
      const [day, month, year] = dateStr.split('/');
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() - 1);  // Offset by one day
      return days[date.getDay()];
    };

    // Step 1: Aggregate the data
    let aggregatedData = {};

    sortedData.forEach(item => {
      const dayOfWeek = getDayOfWeek(item.Date);
      const jobsToProcess = item.jobDetails.slice(0, -1);

      jobsToProcess.forEach(job => {
        // Construct unique key without dayOfWeek
        var uniqueKey = `${sanitizeString(item.FullName)}_${sanitizeString(job.jobDescription)}_${sanitizeString(job.jobNumber)}`;

        // Check if this unique combination already exists
        if (!aggregatedData[uniqueKey]) {
          aggregatedData[uniqueKey] = {
            FullName: sanitizeString(item.FullName),
            "Job Description": sanitizeString(job.jobDescription),
            "Job Number": sanitizeString(job.jobNumber)
          };
        } else if (aggregatedData[uniqueKey][`${dayOfWeek}`]) {
          // If data for that day already exists, then create a new unique key
          let counter = 1;
          while (aggregatedData[`${uniqueKey}_${counter}`] && aggregatedData[`${uniqueKey}_${counter}`][`${dayOfWeek}`]) {
            counter++;
          }
          uniqueKey = `${uniqueKey}_${counter}`;
          aggregatedData[uniqueKey] = {
            FullName: sanitizeString(item.FullName),
            "Job Description": sanitizeString(job.jobDescription),
            "Job Number": sanitizeString(job.jobNumber)
          };
        }

        // Populate or update the appropriate columns based on the dayOfWeek
        aggregatedData[uniqueKey][`${dayOfWeek}`] = sanitizeString(item.Date);
        aggregatedData[uniqueKey][`${dayOfWeek} Start`] = sanitizeString(job.startTime);
        aggregatedData[uniqueKey][`${dayOfWeek} Finish`] = sanitizeString(job.endTime);
        aggregatedData[uniqueKey][`${dayOfWeek} Lunch`] = sanitizeString(job.lunch);
        aggregatedData[uniqueKey][`${dayOfWeek} TotalHours`] = parseFloat(sanitizeString(job.totalHours));
      });
    });

    // Step 2: Populate rows using aggregated data
    for (let key in aggregatedData) {
      worksheet.addRow(aggregatedData[key]);
    }

    // Step 3: Calculating Overtime Hours by Day
    worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {

      if (rowNumber !== 1) {
        days.forEach(day => {
          const totalHoursCell = row.getCell(`${day} TotalHours`);
          const totalHours = totalHoursCell ? totalHoursCell.value : 0;
          let totalOvertime = 0;
          let totalDoubleTime = 0;
          let totalOrd = totalHours;

          // Overtime is anything over 8 ordinary hours
          if (totalHours > 8) {
            totalOrd = 8; // totalHours over 8 means guaranteed 8 ordinary hours
            totalOvertime = totalHours - 8;
          }

          // Doubletime is anything over 2 overtime hours
          if (totalOvertime > 2) {
            totalDoubleTime = totalOvertime - 2;
            totalOvertime = 2;
          }

          const colNumberOrd = worksheet.getColumn(`${day} Ord`).number;
          const colNumberOvertime = worksheet.getColumn(`${day} x1.5`).number;
          const colNumberDouble = worksheet.getColumn(`${day} x2`).number;

          row.getCell(colNumberOrd).value = totalOrd;

          if (totalOvertime !== 0) {
            row.getCell(colNumberOvertime).value = totalOvertime;
          }

          if (totalDoubleTime !== 0) {
            row.getCell(colNumberDouble).value = totalDoubleTime;
          }
        })
      }
    })

    // Step 4: Adding a formula for TOTAL HOURS
    let totalHoursColumnNumber = worksheet.getColumn('TOTAL HOURS').number;
    worksheet.eachRow({ includeEmpty: false }, function (row, rowNumber) {
      if (rowNumber !== 1) { // Exclude header row
        let formula = "=SUM(VALUE(H" + rowNumber + "),VALUE(P" + rowNumber + "),VALUE(X" + rowNumber + "),VALUE(AF" + rowNumber + "),VALUE(AN" + rowNumber + "),VALUE(AV" + rowNumber + "),VALUE(BD" + rowNumber + "))";
        row.getCell(totalHoursColumnNumber).value = { formula: formula };
      }
    });

    // Step 4.1: Adding formula for TOTAL ORD HOURS
    let totalOrdinaryColumnNumber = worksheet.getColumn('Total Ord').number;
    worksheet.eachRow({ includeEmpty: false }, function (row, rowNumber) {
      if (rowNumber !== 1) {
        let formula = "=SUM(VALUE(I" + rowNumber + "),VALUE(Q" + rowNumber + "),VALUE(Y" + rowNumber + "),VALUE(AG" + rowNumber + "),VALUE(AO" + rowNumber + "),VALUE(AW" + rowNumber + "),VALUE(BE" + rowNumber + "))"
        row.getCell(totalOrdinaryColumnNumber).value = { formula: formula };
      }
    })

    // Step 4.2: Adding formula for TOTAL OVERTIME
    let totalOvertimeColumnNumber = worksheet.getColumn('Total x1.5').number;
    worksheet.eachRow({ includeEmpty: false }, function (row, rowNumber) {
      if (rowNumber !== 1) {
        let formula = "=SUM(VALUE(J" + rowNumber + "),VALUE(R" + rowNumber + "),VALUE(Z" + rowNumber + "),VALUE(AH" + rowNumber + "),VALUE(AP" + rowNumber + "),VALUE(AX" + rowNumber + "),VALUE(BF" + rowNumber + "))"
        row.getCell(totalOvertimeColumnNumber).value = { formula: formula };
      }
    })

    // Step 4.3: Adding formula for TOTAL OVERTIME
    let totalDoubleColumnNumber = worksheet.getColumn('Total x2').number;
    worksheet.eachRow({ includeEmpty: false }, function (row, rowNumber) {
      if (rowNumber !== 1) {
        let formula = "=SUM(VALUE(K" + rowNumber + "),VALUE(S" + rowNumber + "),VALUE(AA" + rowNumber + "),VALUE(AI" + rowNumber + "),VALUE(AQ" + rowNumber + "),VALUE(AY" + rowNumber + "),VALUE(BG" + rowNumber + "))"
        row.getCell(totalDoubleColumnNumber).value = { formula: formula };
      }
    })

    // Step 4.4: Populating Rates:
    // TODO: Rates unknown for now
    // let ratesColumnNumber = worksheet.getColumn("Rate").number
    // worksheet.eachRow({includeEmpty: false}, function(row, rowNumber) {
    //   if (rowNumber !== 1) {
    //     row.getCell(ratesColumnNumber).value = 30;
    //   }
    // })

    // Step 4.5: Calculate Total Comp
    let totalCompNumber = worksheet.getColumn("Total Comp").number
    worksheet.eachRow({ includeEmpty: false }, function (row, rowNumber) {
      if (rowNumber !== 1) {
        //              // ORD ---------------------------------------// Overtime --------------------------------------// Double Time
        let formula = `=VALUE(BL${rowNumber})*VALUE(BI${rowNumber}) + VALUE(BL${rowNumber})*VALUE(BJ${rowNumber})*1.5 + VALUE(BL${rowNumber})*VALUE(BK${rowNumber})*2`
        row.getCell(totalCompNumber).value = { formula: formula }
      }
    })

    // Step 5: Coloring columns
    // Define your colors
    const dayColors = {
      'Monday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF96ADC8' } },
      'Tuesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD7FFAB' } },
      'Wednesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCFF6C' } },
      'Thursday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD89D6A' } },
      'Friday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFA6B8' } },
      'Saturday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD28D' } },
      'Sunday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3CCEC' } },
    };

    const darkerShades = {
      'Monday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF768A9F' } },
      'Tuesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8E08A' } },
      'Wednesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E259' } },
      'Thursday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB07757' } },
      'Friday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3748F' } },
      'Saturday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2B76B' } },
      'Sunday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC2A9C9' } },
    };

    // Color the columns
    // Function to apply column color
    function colorColumn(colNumber, color) {
      worksheet.eachRow((row, rowNumber) => {
        if (row.getCell(colNumber)) {
          row.getCell(colNumber).fill = color;
        }
      });
    }

    // Applying column colors based on the day
    days.forEach((day, index) => {
      const startColNumber = 4 + index * 8; // starting from the 4th column (0-based index) and then skipping 5 columns for each day
      for (let i = 0; i < 8; i++) { // For 4 columns of the same day (Date, Start, Finish, Lunch)
        colorColumn(startColNumber + i, dayColors[day]);
      }
      colorColumn(startColNumber + 4, darkerShades[day]); // For the TotalHours column
    });

    // Color the "TOTAL HOURS" column
    const totalHoursColumn = worksheet.getColumn(headers.indexOf("TOTAL HOURS") + 1);
    totalHoursColumn.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3FFF6347' }
      };
    });

    // Color the "TOTAL HOURS" column
    const totalOrdHoursColumn = worksheet.getColumn(headers.indexOf("Total Ord") + 1);
    totalOrdHoursColumn.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3FFF6347' }
      };
    });

    // Color the Overtime Hours column
    const totalOvertimeHoursColumn = worksheet.getColumn(headers.indexOf("Total x1.5") + 1);
    totalOvertimeHoursColumn.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3FFF6347' }
      };
    });

    // Color the Double Hours column
    const totalDoubleHoursColumn = worksheet.getColumn(headers.indexOf("Total x2") + 1);
    totalDoubleHoursColumn.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3FFF6347' }
      };
    });

    // Color the Rate and Total Comp Colors
    const rateColumn = worksheet.getColumn(headers.indexOf("Rate") + 1);
    rateColumn.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
    });

    const totalCompColumn = worksheet.getColumn(headers.indexOf("Total Comp") + 1);
    totalCompColumn.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD3D3D3' }
      };
    });


    // Create a map of unique employee names to colors
    const nameToColorMap = {};
    let currentColorIndex = 0;
    const fullNameColumn = worksheet.getColumn(headers.indexOf("FullName") + 1);
    fullNameColumn.eachCell((cell, rowNumber) => {
      if (rowNumber !== 1) { // Exclude header row
        const employeeName = cell.value;
        if (!nameToColorMap[employeeName]) {
          let dayName = days[currentColorIndex % 7]; // Use modulo to cycle through days
          let color = darkerShades[dayName];
          if (currentColorIndex >= 7) {
            // Increase the middle digit in ARGB to get a different shade.
            let argb = color.fgColor.argb;
            let digitToIncrement = parseInt(argb.slice(3, 4), 16); // Slice the 3rd character from the ARGB
            digitToIncrement = (digitToIncrement + 1) % 16; // Increment and keep it below 16 (Hexadecimal)
            color.fgColor.argb = argb.slice(0, 3) + digitToIncrement.toString(16) + argb.slice(4);
          }
          nameToColorMap[employeeName] = color;
          currentColorIndex++;
        }
      }
    });

    // Now, apply the colors based on the map
    fullNameColumn.eachCell((cell, rowNumber) => {
      if (rowNumber !== 1) { // Exclude header row
        cell.fill = nameToColorMap[cell.value];
      }
    });

    // Save the XLSX file using the file-saver library's saveAs method
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${fileName}.xlsx`);
  }

  // --- EXPORT BUTTON FOR ENGINEERING ---
  const handleExportENG = async (data, timesheetWeek) => {

    // Split on " - " to get start and end dates
    const [startDate, endDate] = timesheetWeek.ts.split(' - ');

    // Extract day and month from each date
    const [startDay, startMonth] = startDate.split('/').slice(0, 2);
    const [endDay, endMonth] = endDate.split('/').slice(0, 2);

    // Construct and return the new string
    const fileName = `Eng Timesheet ${startDay}-${startMonth} to ${endDay}-${endMonth}`;

    // DATA TRANSFORMATION HERE
    const functionData = [...data]; // shallow copy

    const sortedData = functionData.sort((a, b) => {
      if (a.FullName < b.FullName) return -1;
      if (a.FullName > b.FullName) return 1;
      return 0;
    });

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Exported Data');

    // Headers
    const headers = ["FullName", "Job Description", "Job Number"];
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    days.forEach(day => {
      headers.push(day, `${day} Start`, `${day} Finish`, `${day} Lunch`, `${day} TotalHours`);
    });
    headers.push("TOTAL HOURS");
    worksheet.columns = headers.map(header => ({ header, key: header, width: 15 }));

    // Centering columns
    worksheet.getColumn('Job Number').alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getColumn('TOTAL HOURS').alignment = { vertical: 'middle', horizontal: 'center' };

    // Bold the headers
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
    });

    // Function to get day of week from date
    const getDayOfWeek = (dateStr) => {
      const [day, month, year] = dateStr.split('/');
      const date = new Date(year, month - 1, day);
      date.setDate(date.getDate() - 1);  // Offset by one day
      return days[date.getDay()];
    };

    // Step 1: Aggregate the data
    let aggregatedData = {};

    sortedData.forEach(item => {
      const dayOfWeek = getDayOfWeek(item.Date);
      const jobsToProcess = item.jobDetails.slice(0, -1);

      jobsToProcess.forEach(job => {
        // Construct unique key without dayOfWeek
        var uniqueKey = `${sanitizeString(item.FullName)}_${sanitizeString(job.jobDescription)}_${sanitizeString(job.jobNumber)}`;

        // Check if this unique combination already exists
        if (!aggregatedData[uniqueKey]) {
          aggregatedData[uniqueKey] = {
            FullName: sanitizeString(item.FullName),
            "Job Description": sanitizeString(job.jobDescription),
            "Job Number": sanitizeString(job.jobNumber)
          };
        } else if (aggregatedData[uniqueKey][`${dayOfWeek}`]) {
          // If data for that day already exists, then create a new unique key
          let counter = 1;
          while (aggregatedData[`${uniqueKey}_${counter}`] && aggregatedData[`${uniqueKey}_${counter}`][`${dayOfWeek}`]) {
            counter++;
          }
          uniqueKey = `${uniqueKey}_${counter}`;
          aggregatedData[uniqueKey] = {
            FullName: sanitizeString(item.FullName),
            "Job Description": sanitizeString(job.jobDescription),
            "Job Number": sanitizeString(job.jobNumber)
          };
        }

        // Populate or update the appropriate columns based on the dayOfWeek
        aggregatedData[uniqueKey][`${dayOfWeek}`] = sanitizeString(item.Date);
        aggregatedData[uniqueKey][`${dayOfWeek} Start`] = sanitizeString(job.startTime);
        aggregatedData[uniqueKey][`${dayOfWeek} Finish`] = sanitizeString(job.endTime);
        aggregatedData[uniqueKey][`${dayOfWeek} Lunch`] = sanitizeString(job.lunch);
        aggregatedData[uniqueKey][`${dayOfWeek} TotalHours`] = parseFloat(sanitizeString(job.totalHours));
      });
    });

    // Step 2: Populate rows using aggregated data
    for (let key in aggregatedData) {
      worksheet.addRow(aggregatedData[key]);
    }

    // Step 3: Adding a formula for TOTAL HOURS
    let totalHoursColumnNumber = worksheet.getColumn('TOTAL HOURS').number;

    worksheet.eachRow({ includeEmpty: false }, function (row, rowNumber) {
      if (rowNumber !== 1) { // Exclude header row
        let formula = "=SUM(VALUE(H" + rowNumber + "),VALUE(M" + rowNumber + "),VALUE(R" + rowNumber + "),VALUE(W" + rowNumber + "),VALUE(AB" + rowNumber + "),VALUE(AG" + rowNumber + "),VALUE(AL" + rowNumber + "))";;
        row.getCell(totalHoursColumnNumber).value = { formula: formula };
      }
    });

    // Step 4: Coloring columns
    // Define your colors
    const dayColors = {
      'Monday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF96ADC8' } },
      'Tuesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD7FFAB' } },
      'Wednesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCFF6C' } },
      'Thursday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD89D6A' } },
      'Friday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFA6B8' } },
      'Saturday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD28D' } },
      'Sunday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3CCEC' } },
    };

    const darkerShades = {
      'Monday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF768A9F' } },
      'Tuesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB8E08A' } },
      'Wednesday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E259' } },
      'Thursday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB07757' } },
      'Friday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3748F' } },
      'Saturday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2B76B' } },
      'Sunday': { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC2A9C9' } },
    };

    // Color the columns
    // Function to apply column color
    function colorColumn(colNumber, color) {
      worksheet.eachRow((row, rowNumber) => {
        if (row.getCell(colNumber)) {
          row.getCell(colNumber).fill = color;
        }
      });
    }

    // Applying column colors based on the day
    days.forEach((day, index) => {
      const startColNumber = 4 + index * 5; // starting from the 4th column (0-based index) and then skipping 5 columns for each day
      for (let i = 0; i < 4; i++) { // For 4 columns of the same day (Date, Start, Finish, Lunch)
        colorColumn(startColNumber + i, dayColors[day]);
      }
      colorColumn(startColNumber + 4, darkerShades[day]); // For the TotalHours column
    });

    // Color the "TOTAL HOURS" column
    const totalHoursColumn = worksheet.getColumn(headers.indexOf("TOTAL HOURS") + 1);
    totalHoursColumn.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '3FFF6347' }
      };
    });

    // Create a map of unique employee names to colors
    const nameToColorMap = {};
    let currentColorIndex = 0;
    const fullNameColumn = worksheet.getColumn(headers.indexOf("FullName") + 1);
    fullNameColumn.eachCell((cell, rowNumber) => {
      if (rowNumber !== 1) { // Exclude header row
        const employeeName = cell.value;
        if (!nameToColorMap[employeeName]) {
          let dayName = days[currentColorIndex % 7]; // Use modulo to cycle through days
          let color = darkerShades[dayName];
          if (currentColorIndex >= 7) {
            // Increase the middle digit in ARGB to get a different shade.
            let argb = color.fgColor.argb;
            let digitToIncrement = parseInt(argb.slice(3, 4), 16); // Slice the 3rd character from the ARGB
            digitToIncrement = (digitToIncrement + 1) % 16; // Increment and keep it below 16 (Hexadecimal)
            color.fgColor.argb = argb.slice(0, 3) + digitToIncrement.toString(16) + argb.slice(4);
          }
          nameToColorMap[employeeName] = color;
          currentColorIndex++;
        }
      }
    });

    // Now, apply the colors based on the map
    fullNameColumn.eachCell((cell, rowNumber) => {
      if (rowNumber !== 1) { // Exclude header row
        cell.fill = nameToColorMap[cell.value];
      }
    });

    // Save the XLSX file using the file-saver library's saveAs method
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${fileName}.xlsx`);
  }

  // --- SUBMISSIONS MODAL HANDLING


  const [submissionModalOpen, setSubmissionModalOpen] = React.useState(false)

  const handleRenderSubmissionsModal = (data) => {
    setSubmissionModalOpen(!submissionModalOpen)
  }

  return (
    <>
      {division !== "" &&
        <>
          <div className="page-buttons-bar page-buttons-bar-curved">
            <Button sx={{ color: "white" }} onClick={() => { navigate('/') }}> <ArrowBackOutlinedIcon sx={{ marginRight: "5px", fontSize: "1rem" }} /> back</Button>
            <div className="page-function-buttons-container">
              <Button sx={{ color: "white" }} onClick={handleSearch}> <SearchIcon sx={{ marginRight: "5px", fontSize: "1rem" }} /> Search</Button>
              <Button sx={{ color: "white" }} onClick={handleRefresh}> <RefreshOutlinedIcon sx={{ marginRight: "5px", fontSize: "1rem" }} /> Refresh</Button>
            </div>
          </div>
        </>
      }

      {division === "" &&
        <div className="page-buttons-bar">
          {division !== "" ?
            <Button sx={{ color: "white" }} onClick={() => { navigate('/') }}> <ArrowBackOutlinedIcon sx={{ marginRight: "5px", fontSize: "1rem" }} /> back</Button>
            :
            <div className='page-buttons-bar-subtitle' style={{ paddingBottom: "1vh" }}>ENGINEERING</div>}
        </div>
      }
      {showSearchBar && (
        <div className='page-searchbar-box'>
          <Autocomplete
            value={employeeNames.includes(selectedEmployee) ? selectedEmployee : null}
            onChange={(event, newValue) => {
              setSelectedEmployee(newValue);
            }}
            options={employeeNames}
            sx={{ width: "25%" }}
            renderInput={(params) =>
              <TextField
                {...params}
                label="Name"
                InputLabelProps={{
                  sx: {
                    '&.Mui-focused + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'tomato',
                    },
                    '&&.Mui-focused': {
                      color: 'tomato', // color when focused
                    }
                  }
                }}
              />}
          />

          <Autocomplete
            value={jobNumbers.includes(selectedJobNumber) ? selectedJobNumber : null}
            onChange={(event, newValue) => {
              setSelectedJobNumber(newValue);
            }}
            options={jobNumbers}
            sx={{ width: "25%" }}
            renderInput={(params) =>
              <TextField
                {...params}
                label="Job Number"
                InputLabelProps={{
                  sx: {
                    '&.Mui-focused + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'tomato',
                    },
                    '&&.Mui-focused': {
                      color: 'tomato', // color when focused
                    }
                  }
                }}
              />}
          />

          <Autocomplete
            value={dateRanges.includes(selectedDateRange) ? selectedDateRange : null}
            options={dateRanges}
            sx={{ width: "25%" }}
            onChange={(event, newValue) => {
              setSelectedDateRange(newValue);
            }}
            renderInput={(params) =>
              <TextField
                {...params}
                label="Date Range"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: 'tomato',
                    },
                  },
                  '& .MuiInputLabel-outlined': {
                    '&.Mui-focused': {
                      color: 'tomato',
                    },
                  },
                }}
              />
            }
          />
        </div>
      )}

      {Object.keys(filteredList).map((ts, index) => {

        const newHoursPerJob = {};
        const newHoursPerEmployee = {};

        filteredList[ts].forEach(({ FullName, jobDetails }) => {

          // Remove any extra spaces from FullName - 11/08/2023
          FullName = FullName.replace(/\s{2,}/g, ' ').trim();
          jobDetails.forEach(({ jobNumber, totalHours }) => {
            if (jobNumber !== "N/A" && totalHours !== "N/A") {
              if (!newHoursPerJob[jobNumber]) {
                newHoursPerJob[jobNumber] = 0;
              }
              newHoursPerJob[jobNumber] += parseFloat(totalHours);

              if (!newHoursPerEmployee[FullName]) {
                newHoursPerEmployee[FullName] = 0;
              }
              newHoursPerEmployee[FullName] += parseFloat(totalHours);
            }
          });
        });

        return (
          <React.Fragment key={index}>
            <div className="timesheet-card">
              <div className="timesheet-card-title">
                Week: <span className='timesheet-card-text-orange'>{ts}</span>
                <Button
                  sx={{
                    float: "right",
                    marginRight: "1vw",
                    background: "#4CAF50",
                    color: "white",
                    border: "1px solid #4CAF50",
                    "&:hover": {
                      background: "#43A047",
                      color: "white",
                    }
                  }}
                  onClick={() => filterExportViewer(filteredList[ts], { ts })}
                >
                  <FileDownloadIcon sx={{ marginRight: "5px", fontSize: "1rem" }} /> Export
                </Button>
                {/* <Button
                  sx={{
                    float: "right",
                    marginRight: "1vw",
                    background: "#4CAF50",
                    color: "white",
                    border: "1px solid #4CAF50",
                    "&:hover": {
                      background: "#43A047",
                      color: "white",
                    }
                  }}
                  onClick={()=>handleRenderSubmissionsModal(filteredList[ts])}
                >
                  <AllInboxIcon sx={{ marginRight: "5px", fontSize: "1rem" }} /> Submissions
                </Button> */}
              </div>
              <div className="timesheet-summary-container">
                <div className="timesheet-summary">
                  <div className="timesheet-card-subtitle timesheet-card-subtitle-total">Total Job Hours</div>
                  <div className="entry-container-jobs">
                    {Object.entries(newHoursPerJob).map(([jobNumber, hours], idx) => (
                      <div className="job-entry" key={`job-${idx}`}><span className='timesheet-card-subtitle'>{jobNumber}:</span> {hours}</div>
                    ))}
                  </div>
                </div>
                <div className="timesheet-summary">
                  <div className="timesheet-card-subtitle timesheet-card-subtitle-total">Total Employee Hours</div>
                  <div className="entry-container-employee">
                    {Object.entries(newHoursPerEmployee).map(([employeeName, hours], idx) => (
                      <div className="employee-entry" key={`employee-${idx}`}><span className='timesheet-card-subtitle'>{employeeName}:</span> {hours}</div>
                    ))}
                  </div>
                </div>
              </div>
              {
                viewer === "accounts" &&
                <>

                </>
              }

              {
                viewer === "engineering" &&
                <>
                  <ENGTimesheetJob weekData={filteredList[ts]} />
                  <ENGTimesheetDaily weekData={filteredList[ts]} refreshPostUpdate={handleRefresh} />
                  <ENGTimesheetsSubmissions weekData={filteredList[ts]}/>
                </>
              }
            </div>
            <br />
          </React.Fragment>
        );
      })}
    </>
  )
}
